// Data normalization utilities for consistent data handling across the app

/**
 * Normalize pickup status to handle variations in status naming
 */
export const normalizePickupStatus = (status: string): string => {
  if (!status) return 'pending';
  
  const normalized = status.toLowerCase().replace(/[_\s-]/g, '');
  
  const pickupStatusMap: Record<string, string> = {
    // New pickup status enum values
    'pending': 'pending',
    'pickuprequested': 'pickup-requested',
    'assigned': 'assigned',
    'pickedup': 'picked-up',
    'arrivedatfacility': 'arrived-at-facility',
    'completed': 'completed',
    
    // Legacy mappings
    'intransit': 'picked-up',
    'outfordelivery': 'picked-up', 
    'inprocess': 'assigned',
    'outofdelivery': 'picked-up',
    'delivered': 'completed'
  };
  
  return pickupStatusMap[normalized] || status;
};

/**
 * Normalize delivery status to handle variations in status naming
 */
export const normalizeDeliveryStatus = (status: string): string => {
  if (!status) return 'pending';
  
  const normalized = status.toLowerCase().replace(/[_\s-]/g, '');
  
  const deliveryStatusMap: Record<string, string> = {
    // New delivery status enum values
    'pending': 'pending',
    'plannedfordelivery': 'planned-for-delivery',
    'inroute': 'in-route',
    'delivered': 'delivered',
    'failed': 'failed',
    'cancelled': 'cancelled',
    
    // Legacy mappings
    'intransit': 'in-route',
    'outfordelivery': 'in-route',
    'inprocess': 'planned-for-delivery',
    'outofdelivery': 'in-route',
    'completed': 'delivered'
  };
  
  return deliveryStatusMap[normalized] || status;
};

/**
 * Normalize order status to handle variations in status naming (legacy support)
 */
export const normalizeOrderStatus = (status: string): string => {
  if (!status) return 'unknown';
  
  const normalized = status.toLowerCase().replace(/[_\s-]/g, '');
  
  const statusMap: Record<string, string> = {
    'intransit': 'in-transit',
    'outfordelivery': 'in-transit', 
    'inprocess': 'in-transit',
    'pickuprequested': 'pickup-requested',
    'pickedup': 'picked-up',
    'outofdelivery': 'in-transit'
  };
  
  return statusMap[normalized] || status;
};

/**
 * Check if order is in transit (flexible status matching)
 */
export const isOrderInTransit = (order: any): boolean => {
  const pickupStatus = normalizePickupStatus(order?.pickupStatus || '').toLowerCase();
  const deliveryStatus = normalizeDeliveryStatus(order?.deliveryStatus || '').toLowerCase();
  const legacyStatus = normalizeOrderStatus(order?.status || '').toLowerCase();
  
  return pickupStatus === 'picked-up' || 
         pickupStatus === 'arrived-at-facility' ||
         deliveryStatus === 'in-route' || 
         legacyStatus === 'in-transit';
};

/**
 * Check if order pickup is completed
 */
export const isPickupCompleted = (order: any): boolean => {
  const pickupStatus = normalizePickupStatus(order?.pickupStatus || '').toLowerCase();
  return pickupStatus === 'completed';
};

/**
 * Check if order pickup is requested
 */
export const isPickupRequested = (order: any): boolean => {
  const pickupStatus = normalizePickupStatus(order?.pickupStatus || '').toLowerCase();
  return pickupStatus === 'pickup-requested';
};

/**
 * Check if order is ready for delivery
 */
export const isReadyForDelivery = (order: any): boolean => {
  const pickupStatus = normalizePickupStatus(order?.pickupStatus || '').toLowerCase();
  const deliveryStatus = normalizeDeliveryStatus(order?.deliveryStatus || '').toLowerCase();
  
  return pickupStatus === 'completed' && 
         (deliveryStatus === 'pending' || deliveryStatus === 'planned-for-delivery');
};

/**
 * Check if order delivery is completed
 */
export const isDeliveryCompleted = (order: any): boolean => {
  const deliveryStatus = normalizeDeliveryStatus(order?.deliveryStatus || '').toLowerCase();
  return deliveryStatus === 'delivered';
};

/**
 * Normalize order data to handle field name variations across APIs
 */
export const normalizeOrderData = (order: any) => {
  if (!order) return null;
  
  return {
    // ID fields - prioritize MongoDB ObjectId
    _id: order._id || order.id,
    id: order.id || order._id,
    orderNumber: order.orderNumber || order.prescriptionNumber,
    prescriptionNumber: order.prescriptionNumber || order.orderNumber,
    
    // Customer/Patient info
    customerName: order.customerName || order.patientName,
    customerPhone: order.customerPhone || order.patientPhone,  
    customerAddress: order.customerAddress || order.patientAddress,
    patientName: order.patientName || order.customerName,
    patientPhone: order.patientPhone || order.customerPhone,
    patientAddress: order.patientAddress || order.customerAddress,
    
    // Status and delivery info
    status: normalizeOrderStatus(order.pickupStatus || order.status),
    pickupStatus: normalizePickupStatus(order.pickupStatus || order.status),
    deliveryStatus: normalizeDeliveryStatus(order.deliveryStatus || order.status),
    deliveryType: order.deliveryType,
    deliveryDate: order.deliveryDate,
    deliveryTime: order.deliveryTime,
    
    // Medications
    medicines: order.medicines || [],
    medications: order.medications || (order.medicines ? order.medicines.map((m: any) => m.name || m.medicineName).filter(Boolean) : []),
    
    // Financial
    totalAmount: order.totalAmount || order.totalValue || order.value || order.totalPrice,
    value: order.value || order.totalAmount || order.totalValue || order.totalPrice,
    
    // Timestamps
    createdAt: order.createdAt,
    updatedAt: order.updatedAt,
    orderDate: order.orderDate || order.createdAt,
    
    // Delivery details
    delivery: order.delivery,
    proofType: order.proofType,
    proofData: order.proofData,
    
    // Preserve original fields
    ...order
  };
};

/**
 * Normalize delivery statistics to handle variations
 */
export const normalizeDeliveryStats = (stats: any) => {
  if (!stats) return null;
  
  return {
    totalOrders: stats.totalOrders || stats.total || 0,
    inTransit: stats.inTransit || stats.in_transit || stats.inProgress || 0,
    delivered: stats.delivered || stats.completed || 0,
    pending: stats.pending || 0,
    failed: stats.failed || stats.cancelled || 0,
    successRate: stats.successRate || stats.success_rate || 0,
    avgDeliveryTime: stats.avgDeliveryTime || stats.averageDeliveryTime || 0,
    ...stats
  };
};

/**
 * Normalize analytics data structure
 */
export const normalizeAnalyticsData = (data: any) => {
  if (!data) return null;
  
  return {
    basicStats: normalizeDeliveryStats(data.basicStats),
    deliveryInsights: data.deliveryInsights || [],
    recentOrders: (data.recentOrders || []).map(normalizeOrderData),
    hourlyData: data.hourlyData || [],
    deliveryTypeBreakdown: data.deliveryTypeBreakdown,
    medicationCategories: data.medicationCategories || [],
    patientDemographics: data.patientDemographics || [],
    revenueData: data.revenueData || [],
    orderStatus: data.orderStatus || [],
    satisfactionData: data.satisfactionData || [],
    customerRetention: data.customerRetention,
    equipmentUsage: data.equipmentUsage,
    growthMetrics: data.growthMetrics,
    operationalKPIs: data.operationalKPIs,
    ...data
  };
};

/**
 * Get time ago string for consistent time display
 */
export const getTimeAgo = (dateString: string): string => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  } catch (error) {
    console.warn('Invalid date string:', dateString);
    return 'Invalid date';
  }
};

/**
 * Find the correct MongoDB ObjectId from order object
 */
export const findMongoObjectId = (order: any): string | null => {
  if (!order) return null;
  
  // Try different possible fields for MongoDB ObjectId
  const possibleFields = ['_id', 'id', 'mongoId', 'objectId'];
  
  for (const field of possibleFields) {
    const value = order[field];
    if (value && typeof value === 'string' && value.length === 24 && /^[0-9a-fA-F]{24}$/.test(value)) {
      return value;
    }
  }
  
  // If no valid ObjectId found, log warning and return null
  console.warn('No valid MongoDB ObjectId found in order:', order);
  return null;
};

/**
 * Debug data consistency - commented out for production
 */
// export const debugDataConsistency = (label: string, data: any) => {
//   console.group(`🔍 Data Consistency Debug: ${label}`);
//   
//   if (Array.isArray(data)) {
//     console.log(`📊 Total items: ${data.length}`);
//     
//     if (data.length > 0) {
//       console.log(`📋 Sample item structure:`, Object.keys(data[0]));
//       console.log(`🔍 First item:`, data[0]);
//       
//       // Check for status distribution if it's orders
//       if (data[0]?.status) {
//         const statusCounts = data.reduce((counts: any, item: any) => {
//           const status = item.status || 'unknown';
//           counts[status] = (counts[status] || 0) + 1;
//           return counts;
//         }, {});
//         console.log(`📈 Status distribution:`, statusCounts);
//         
//         // Check in-transit variations
//         const inTransitCount = data.filter(isOrderInTransit).length;
//         console.log(`🚛 In-transit orders (normalized): ${inTransitCount}`);
//       }
//     }
//   } else if (data && typeof data === 'object') {
//     console.log(`📋 Object keys:`, Object.keys(data));
//     console.log(`🔍 Object data:`, data);
//   } else {
//     console.log(`📋 Raw data:`, data);
//   }
//   
//   console.groupEnd();
// };

